import React, {
 useState
} from 'react';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 ChevronDown, ChevronRight, Braces
} from 'lucide-react';
import {
    WorkflowStep, FetchStepConfig,
} from '../../types/workflow.types';

interface PlaceholderHelperProps {
    steps: WorkflowStep[];
    currentStepIndex: number;
    onInsert: (placeholder: string) => void;
}

function getStepOutputFields(step: WorkflowStep): string[] {
    switch (step.type) {
        case 'FETCH': {
            const config = step.config as FetchStepConfig;
            return config.fields.length > 0 ? config.fields : ['id', 'name'];
        }
        case 'API_CALL':
            return ['_output', '_compiled_output'];
        case 'MUTATE':
            return ['_affected_rows', '_result'];
        default:
            return [];
    }
}

const PlaceholderHelper: React.FC<PlaceholderHelperProps> = ({
 steps, currentStepIndex, onInsert
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});

    const priorSteps = steps.slice(0, currentStepIndex);

    if (priorSteps.length === 0) return null;

    const toggleStep = (stepId: string) => {
        setExpandedSteps((prev) => ({
 ...prev,
[stepId]: !prev[stepId]
}));
    };

    return (
        <div className="border rounded-md bg-muted/20">
            <button
                type="button"
                className="flex items-center gap-2 w-full p-3 text-sm font-medium hover:bg-muted/40 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
            >
                <Braces className="h-3.5 w-3.5" />
                Available Data
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 ml-auto" /> : <ChevronRight className="h-3.5 w-3.5 ml-auto" />}
            </button>

            {isOpen && (
                <div className="px-3 pb-3 space-y-1">
                    {priorSteps.map((step, idx) => {
                        const fields = getStepOutputFields(step);
                        const isExpanded = expandedSteps[step.id] !== false; // default open

                        return (
                            <div key={step.id} className="border rounded bg-background">
                                <button
                                    type="button"
                                    className="flex items-center gap-2 w-full p-2 text-xs hover:bg-muted/30 transition-colors"
                                    onClick={() => toggleStep(step.id)}
                                >
                                    {isExpanded
                                        ? <ChevronDown className="h-3 w-3 shrink-0" />
                                        : <ChevronRight className="h-3 w-3 shrink-0" />}
                                    <span className="font-medium">Step {idx + 1}:</span>
                                    <span className="truncate">{step.name}</span>
                                    <Badge variant="outline" className="text-[10px] ml-auto shrink-0">
                                        {step.type.replace('_', ' ')}
                                    </Badge>
                                </button>
                                {isExpanded && fields.length > 0 && (
                                    <div className="flex flex-wrap gap-1 px-2 pb-2 pl-7">
                                        {fields.map((field) => (
                                            <Badge
                                                key={field}
                                                variant="secondary"
                                                className="text-[10px] cursor-pointer hover:bg-primary/10 transition-colors"
                                                onClick={() => onInsert(`{{step_${idx + 1}.${field}}}`)}
                                            >
                                                {field}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <p className="text-[10px] text-muted-foreground pt-1">
                        Click a field to insert its placeholder.
                    </p>
                </div>
            )}
        </div>
    );
};

export default PlaceholderHelper;
